<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "Does this receipt add up, and what am I about to book?"
  Answer:   the mismatch banner - the interesting moment of this whole screen - appears the
            instant a stated total and a sum of items disagree, live, as the user edits either
            side. It is a StatusTag (icon plus tinted surface, never colour alone), and it is
            the one thing standing between the user and the commit button.
  Context:  the photograph itself, and the merchant/date/total fields it was read from -
            regular weight, so the reviewer can check them against what the image shows.
  Controls: "Create entry" is the one --color-action control above the fold; add/remove item
            and the per-line category and bought-by pickers are ordinary bordered controls, matched to the
            item table's own row density so editing feels like a spreadsheet, not a dialog.
-->
<script setup lang="ts">
import type { ReceiptCategorySuggestion, ReceiptScanJob } from '../internal/types.ts'
import { requireCurrencyCode } from '@taler/domain'
import { ActionButton, Card, StatusTag, TextField } from '@taler/ui'
import { computed, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import { AccountPicker } from '@/features/entries'
import {
  draftCanCommit,
  draftFromJob,
  draftMismatch,
  newLineItem,
  splitLineTarget,
  toEditInput,
} from '../internal/draft.ts'
import { failureMessageKey } from '../internal/failure-message.ts'

const props = defineProps<{
  job: ReceiptScanJob
  suggestions: readonly ReceiptCategorySuggestion[]
  contactAccountIds: ReadonlySet<string>
  imageUrl: string | undefined
  isSaving: boolean
  isCommitting: boolean
  isRescanning: boolean
  saveErrorDetail: string | undefined
  commitErrorDetail: string | undefined
}>()

const emit = defineEmits<{
  commit: [ReturnType<typeof toEditInput>]
  rescan: []
  viewEntry: [string]
}>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')
const jobCurrency = computed(() => requireCurrencyCode(props.job.currency))

const draft = reactive(draftFromJob(props.job, props.contactAccountIds))
watch(
  () => props.job.id,
  () => Object.assign(draft, draftFromJob(props.job, props.contactAccountIds)),
)
// Contacts may load after the job.
watch(
  () => props.contactAccountIds,
  (ids) => {
    for (const line of draft.lineItems)
      Object.assign(line, splitLineTarget(line.boughtBy ?? line.accountId, ids))
  },
)

/** Fills in a line's category from a suggestion only while that line has none of its own -
 * never overwrites a category extraction already carried or the reviewer already picked. */
watch(
  () => props.suggestions,
  (suggestions) => {
    for (const suggestion of suggestions) {
      const line = draft.lineItems[suggestion.lineIndex]
      if (line !== undefined && line.accountId === undefined) line.accountId = suggestion.accountId
    }
  },
  { immediate: true },
)

const mismatch = computed(() => draftMismatch(draft, props.job.currency))
const canCommit = computed(
  () => draftCanCommit(draft, props.job.currency) && !props.isSaving && !props.isCommitting,
)

function addLine(): void {
  draft.lineItems.push(newLineItem())
}

function removeLine(key: string): void {
  draft.lineItems = draft.lineItems.filter((item) => item.key !== key)
}

function onCommit(): void {
  emit('commit', toEditInput(draft, props.job.currency))
}
</script>

<template>
  <div class="flex flex-col gap-[var(--space-block)]">
    <StatusTag v-if="job.status === 'COMMITTED'" tone="ok">
      {{ t('receiptScan.committedNotice') }}
    </StatusTag>
    <div
      v-else-if="job.status === 'FAILED' || job.status === 'NEEDS_REVIEW'"
      class="flex flex-wrap items-center gap-[var(--space-block)]"
    >
      <StatusTag v-if="job.status === 'FAILED'" tone="danger">
        {{ t(failureMessageKey(job.failureCode)) }}
      </StatusTag>
      <StatusTag v-else tone="neutral">{{ t('receiptScan.needsReviewNotice') }}</StatusTag>
      <ActionButton variant="secondary" :disabled="isRescanning" @click="emit('rescan')">
        {{ isRescanning ? t('receiptScan.rescanning') : t('receiptScan.rescan') }}
      </ActionButton>
    </div>

    <p v-if="job.status !== 'FAILED'" class="text-[color:var(--color-text-muted)]">
      {{ t('receiptScan.cannotDelete') }}
      <button
        v-if="job.postedEntryId !== undefined"
        type="button"
        class="text-[color:var(--color-accent)] underline"
        @click="emit('viewEntry', job.postedEntryId)"
      >
        {{ t('receiptScan.viewEntryAndReverse') }}
      </button>
    </p>
    <p v-else class="text-[color:var(--color-text-muted)]">{{ t('receiptScan.enterManually') }}</p>

    <fieldset :disabled="job.status === 'COMMITTED'" class="flex min-w-0 flex-col gap-[var(--space-block)]">
      <div class="flex flex-wrap items-start gap-[var(--space-block)]">
        <img
          v-if="imageUrl !== undefined"
          :src="imageUrl"
          :alt="t('receiptScan.previewAlt')"
          class="max-h-96 w-fit max-w-80 rounded-[var(--radius-surface)] border border-[color:var(--color-border)] object-contain bg-[color:var(--color-surface)]"
        />
        <div class="grid min-w-80 flex-1 grid-cols-3 gap-[var(--space-cell)]">
          <label class="flex flex-col gap-1">
            <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
              t('receiptScan.date')
            }}</span>
            <input
              v-model="draft.receiptDate"
              type="date"
              :aria-label="t('receiptScan.date')"
              class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
            />
          </label>
          <TextField
            v-model="draft.merchant"
            :label="t('receiptScan.payee')"
            :maxlength="255"
            :placeholder="t('receiptScan.payeePlaceholder')"
          />
          <label class="flex flex-col gap-1">
            <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
              t('receiptScan.statedTotal')
            }}</span>
            <AmountInput
              v-model="draft.statedTotal"
              :currency="jobCurrency"
              :locale="currentLocale"
              :aria-label="t('receiptScan.statedTotal')"
            />
          </label>
        </div>
      </div>

      <Card padding="none" class="overflow-x-auto">
        <table class="w-full min-w-[56rem] border-collapse">
          <thead>
            <tr class="table-head">
              <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
                {{ t('receiptScan.itemName') }}
              </th>
              <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
                {{ t('receiptScan.lineCategory') }}
              </th>
              <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
                {{ t('receiptScan.boughtBy') }}
              </th>
              <th class="px-[var(--space-cell)] py-[var(--space-inline)] text-right" scope="col">
                {{ t('receiptScan.amount') }}
              </th>
              <th class="px-[var(--space-cell)] py-[var(--space-inline)] text-right" scope="col">
                {{ t('receiptScan.quantity') }}
              </th>
              <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
                <span class="sr-only">{{ t('receiptScan.rowActions') }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in draft.lineItems"
              :key="item.key"
              class="border-b border-[color:var(--color-border)]"
            >
              <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
                <input
                  v-model="item.description"
                  type="text"
                  maxlength="255"
                  :aria-label="t('receiptScan.itemName')"
                  class="min-h-[var(--density-control)] w-full rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-inline)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
                />
              </td>
              <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
                <span
                  v-if="item.boughtBy !== undefined"
                  class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
                >
                  {{ t('receiptScan.owedByContact') }}
                </span>
                <AccountPicker
                  v-else
                  :id="`receipt-line-category-${item.key}`"
                  :label="t('receiptScan.lineCategory')"
                  :model-value="item.accountId ?? null"
                  filter-class="EXPENSE"
                  class="w-48"
                  @update:model-value="(value) => (item.accountId = value ?? undefined)"
                />
              </td>
              <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
                <AccountPicker
                  v-if="!item.isDiscount"
                  :id="`receipt-line-bought-by-${item.key}`"
                  :label="t('receiptScan.boughtBy')"
                  :model-value="item.boughtBy ?? null"
                  contacts-only
                  compact
                  :placeholder="t('receiptScan.boughtByYou')"
                  class="w-44"
                  @update:model-value="(value) => (item.boughtBy = value ?? undefined)"
                />
              </td>
              <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
                <AmountInput
                  v-model="item.amount"
                  :currency="jobCurrency"
                  :locale="currentLocale"
                  :show-total="false"
                  :aria-label="t('receiptScan.amount')"
                  class="w-32"
                />
              </td>
              <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
                <input
                  v-model.number="item.quantity"
                  type="number"
                  min="1"
                  :aria-label="t('receiptScan.quantity')"
                  class="min-h-[var(--density-control)] w-20 rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-inline)] text-right text-[length:var(--text-step-3)] tabular-nums text-[color:var(--color-text)]"
                />
              </td>
              <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
                <button
                  type="button"
                  :aria-label="t('receiptScan.removeItem')"
                  class="min-h-[var(--density-target-min)] min-w-[var(--density-target-min)] rounded-[var(--radius-control)] px-[var(--space-tight)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger)]"
                  @click="removeLine(item.key)"
                >
                  ×
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </Card>

      <div>
        <button
          type="button"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] px-[var(--space-tight)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
          @click="addLine"
        >
          {{ t('receiptScan.addItem') }}
        </button>
      </div>

      <StatusTag v-if="mismatch === true" tone="danger" role="alert">
        {{ t('receiptScan.mismatchWarning') }}
      </StatusTag>

      <StatusTag
        v-if="saveErrorDetail !== undefined || commitErrorDetail !== undefined"
        tone="danger"
        role="alert"
      >
        {{ commitErrorDetail ?? saveErrorDetail }}
      </StatusTag>

      <div class="flex items-center gap-[var(--space-group)]">
        <ActionButton
          size="lg"
          type="button"
          :disabled="!canCommit"
          class="text-[length:var(--text-step-3)] font-medium"
          @click="onCommit"
        >
          {{ isSaving || isCommitting ? t('receiptScan.committing') : t('receiptScan.confirmCreate') }}
        </ActionButton>
      </div>
    </fieldset>
  </div>
</template>
